import { LitElement, html, css } from 'lit'

class MobileMenu extends LitElement {
  static styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: center;
      position: fixed;
      inset: 68px 0 0 0;
      z-index: 99;
    }
    :host([state='closed']) {
      display: none;
    }
    dialog {
      display: block;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      background: #fff;
      z-index: 100;
      opacity: 1;
      transform: translateY(0);
      transition: opacity 150ms ease, transform 150ms ease;
    }
    :host([state='opening']) dialog,
    :host([state='closing']) dialog {
      opacity: 0;
      transform: translateY(10px);
    }
  `
  static properties = {
    state: { type: String, reflect: true },
  }
  constructor() {
    super()
    this.state = 'closed'
    this.handleEscPress = this.handleEscPress.bind(this)
    this.handleTriggerClick = this.handleTriggerClick.bind(this)
    this.checkForScroll = this.checkForScroll.bind(this)
    this.closeDialog = this.closeDialog.bind(this)
    this._trigger = document.querySelector(
      `[aria-controls="${this.getAttribute('id')}"]`
    )
    this.initTrigger()
    document.body.prepend(this)
  }
  firstUpdated() {
    if (this.state === 'open') {
      this.initDialog()
    }
  }
  updated(changedProperties) {
    if (changedProperties.has('state')) {
      if (this.state === 'open') {
        this.initDialog()
      }
      if (this.state === 'closed') {
        this.closeDialog()
      }
    }
  }
  initTrigger() {
    this._trigger.setAttribute('aria-haspopup', 'true')
    this._trigger.setAttribute('aria-expanded', 'false')
    this._trigger.addEventListener('click', this.handleTriggerClick)
  }
  initDialog() {
    this._trigger.setAttribute('aria-expanded', 'true')
    document.querySelector('.main').setAttribute('inert', '')
    document.querySelector('.footer').setAttribute('inert', '')
    setTimeout(() => {
      this.state = 'open'
      this.querySelector('input')?.focus()
      document.addEventListener('click', this.closeDialog)
      window.addEventListener('keyup', this.handleEscPress)
      document.dispatchEvent(new CustomEvent('modal-opened'))
    }, 50)

    this.checkForScroll()
    this.addEventListener('menu-item-toggled', this.checkForScroll)
    window.addEventListener('resize', this.checkForScroll)

    document.documentElement.style.overscrollBehavior = 'none'
  }
  checkForScroll(e) {
    const inner = this.querySelector('.mobile-nav__inner')
    if (inner.scrollHeight - 116 > inner.clientHeight) {
      inner.classList.add('mobile-nav__inner--shadow')
    } else {
      inner.classList.remove('mobile-nav__inner--shadow')
    }
  }
  closed() {
    document.querySelector('.main').removeAttribute('inert', '')
    document.querySelector('.footer').removeAttribute('inert', '')
    document.documentElement.style.removeProperty('overscroll-behavior')
    document.removeEventListener('click', this.closeDialog)
    window.removeEventListener('keyup', this.handleEscPress)
    window.removeEventListener('resize', this.checkForScroll)
    this.removeEventListener('menu-item-toggled', this.checkForScroll)
    this.dispatchEvent(new CustomEvent('modal-closed'))
    this._trigger.focus()
  }
  closeDialog() {
    this._trigger.setAttribute('aria-expanded', 'false')
    this.dispatchEvent(new CustomEvent('modal-closing'))
    setTimeout(() => {
      this.closed()
      this.state = 'closed'
    }, 150)
  }
  handleTriggerClick(e) {
    e.preventDefault()
    if (this.state === 'open') {
      this.state = 'closing'
      this.closeDialog()
    } else {
      this.state = 'opening'
      this.initDialog()
    }
  }
  handleEscPress(e) {
    if (e.key === 'Escape') {
      this.closeDialog()
    }
  }
  render() {
    if (this.state === 'closed') return

    return html`
      <dialog part="dialog" open @click="${(e) => e.stopPropagation()}">
        <slot></slot>
      </dialog>
    `
  }
}
customElements.define('mobile-menu', MobileMenu)

class MobileMenuItem extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    div {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1em;
    }
    button {
      background: none;
      border: none;
      padding: 1em;
      cursor: pointer;
      appearance: none;
    }
    button:focus-visible {
      outline: var(--outline);
      outline-offset: 0;
      outline-color: var(--focus-color);
      border-radius: 100px;
    }
  `
  static properties = {
    expanded: { type: Boolean, reflect: true },
  }
  firstUpdated() {
    this._toggleButton = this.shadowRoot.querySelector('button')
    if (this._toggleButton) {
      this._toggleButton.setAttribute('aria-expanded', 'false')
      this._id = Math.random().toString(36).substring(2, 15)
      this._toggleButton.setAttribute('aria-controls', this._id)
    }
  }
  updated(changedProperties) {
    if (changedProperties.has('expanded')) {
      if (this._toggleButton) {
        this._toggleButton.setAttribute('aria-expanded', this.expanded)
        this.dispatchEvent(
          new CustomEvent('menu-item-toggled', {
            detail: this.expanded,
            bubbles: true,
          })
        )
      }
    }
  }
  render() {
    let children = this.expanded
      ? html`<slot name="children" id="${this._id}"></slot>`
      : ''
    let toggleButton = this.querySelector('ul')
      ? html`
          <button @click="${this.toggleChildren}" part="toggle">
            <toggle-icon ?open="${this.expanded}"></toggle-icon>
          </button>
        `
      : ''
    return html`
      <div>
        <slot name="parent"></slot>
        ${toggleButton}
      </div>
      ${children}
    `
  }
  toggleChildren() {
    this.expanded = !this.expanded
  }
}
customElements.define('mobile-menu-item', MobileMenuItem)
